import { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '@/components/base/Button';
import StatCard from '@/components/base/StatCard';
import { useRole } from '@/context/RoleContext';
import { api, apiList } from '@/utils/api';
import { formatMoney } from '@/utils/format';
import { filterWallets, sortTopUps, walletTotals } from '@/utils/wallet';
import { currenciesForMarkets } from '@/utils/markets';
import type { Wallet, WalletTopUp } from '@/types/portal';
import WalletToolbar from './components/WalletToolbar';
import FundingWalletList from './components/FundingWalletList';
import WalletAttentionPanel from './components/WalletAttentionPanel';
import WalletQuickActions from './components/WalletQuickActions';
import TopUpModal, { type FundingOptions, type MethodId } from './components/TopUpModal';
import WalletManageModal, { type WalletAction } from './components/WalletManageModal';
import FundingHelpModal from './components/FundingHelpModal';
import FundingBalanceCard from './components/FundingBalanceCard';

const messageOf = (error: unknown, fallback: string) => (error instanceof Error ? error.message : fallback);

export default function Wallets() {
  const { can } = useRole();
  const canFund = can('wallets.fund');
  const canManage = can('wallets.manage');

  const [currency, setCurrency] = useState('All currencies');
  const [markets, setMarkets] = useState<string[]>([]);
  const [query, setQuery] = useState('');
  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [funding, setFunding] = useState<FundingOptions | null>(null);
  const [fundingError, setFundingError] = useState('');
  const [topUps, setTopUps] = useState<WalletTopUp[]>([]);
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [topUpWallet, setTopUpWallet] = useState<Wallet | null>(null);
  const [topUpDone, setTopUpDone] = useState(false);
  const [topUpRef, setTopUpRef] = useState('');
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpWallet, setHelpWallet] = useState<Wallet | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [notice, setNotice] = useState('');
  const [managed, setManaged] = useState<{ wallet: Wallet; action: WalletAction } | null>(null);
  const [managing, setManaging] = useState(false);

  // The agency's funding wallets and each one's top-ups.
  const loadWallets = useCallback(
    () =>
      apiList<Wallet>('/wallets?ownerType=Funding')
        .then(async ({ data }) => {
          setWallets(data);
          const lists = await Promise.all(
            data.map((wallet) =>
              apiList<WalletTopUp>(`/wallets/${wallet.id}/top-ups`)
                .then((reply) => reply.data)
                .catch(() => [] as WalletTopUp[]),
            ),
          );
          setTopUps(sortTopUps(lists.flat()));
        })
        .catch((loadError: unknown) => setError(messageOf(loadError, 'Could not load the wallets.'))),
    [],
  );

  // How a wallet can be funded: production has no funding methods, so Fund wallet explains how instead.
  useEffect(() => {
    void loadWallets().finally(() => setLoading(false));
    Promise.all([
      api<Pick<FundingOptions, 'methods' | 'mobileMoneyProviders'>>('/funding/methods'),
      api<FundingOptions['bankAccounts']>('/funding/bank-accounts'),
    ])
      .then(([methods, bankAccounts]) => setFunding({ ...methods, bankAccounts }))
      .catch((loadError: unknown) => setFundingError(messageOf(loadError, 'Funding a wallet is not available.')));
  }, [loadWallets]);

  const refresh = () => {
    setRefreshing(true);
    setError('');
    void loadWallets().finally(() => setRefreshing(false));
  };

  const fundingOnly = useMemo(() => wallets.filter((wallet) => wallet.ownerType === 'Funding'), [wallets]);

  const fundingWallets = useMemo(
    () => filterWallets(wallets, { owner: 'Funding', currency, markets, query }),
    [wallets, currency, markets, query],
  );

  const currencyOptions = useMemo(
    () =>
      markets.length > 0
        ? ['All currencies', ...currenciesForMarkets(markets)]
        : ['All currencies', ...new Set(fundingOnly.map((wallet) => wallet.currency))],
    [markets, fundingOnly],
  );

  const totals = walletTotals(wallets);

  const openTopUp = (wallet: Wallet | null) => {
    // Never show funding instructions the API didn't supply: production has none, so explain how Twiva funds it.
    if (!funding) {
      if (!fundingError) {
        setError('Funding options are still loading.');
        return;
      }
      setHelpWallet(wallet ?? fundingOnly[0] ?? null);
      setHelpOpen(true);
      return;
    }
    setTopUpDone(false);
    setTopUpRef('');
    setTopUpWallet(wallet);
    setTopUpOpen(true);
  };

  const confirmTopUp = async (
    wallet: Wallet,
    amount: number,
    methodLabel: string,
    _status: 'Processing' | 'Completed',
    method: MethodId,
  ) => {
    setError('');
    try {
      const topUp = await api<WalletTopUp>(`/wallets/${wallet.id}/top-ups`, {
        method: 'POST',
        headers: { 'Idempotency-Key': crypto.randomUUID() },
        body: JSON.stringify({ method, amount }),
      });
      setTopUpRef(topUp.reference);
      // The modal's label carries the bank, provider or card digits the API leaves out.
      setTopUps((current) => sortTopUps([{ ...topUp, method: methodLabel }, ...current]));
      setTopUpDone(true);
      setNotice(`${formatMoney(amount, wallet.currency)} top-up initiated · ${topUp.reference}.`);
    } catch (topUpError) {
      setTopUpOpen(false);
      setError(messageOf(topUpError, 'Could not start the top-up.'));
    }
  };

  const replaceWallet = (updated: Wallet) =>
    setWallets((current) => current.map((wallet) => (wallet.id === updated.id ? updated : wallet)));

  /** Runs one wallet change, closes the dialog and reports the outcome. */
  const manage = async (run: () => Promise<string>) => {
    setManaging(true);
    setError('');
    try {
      setNotice(await run());
    } catch (manageError) {
      setError(messageOf(manageError, 'Could not update the wallet.'));
    } finally {
      setManaging(false);
      setManaged(null);
    }
  };

  const saveWallet = (wallet: Wallet, details: { name: string; description: string }) =>
    manage(async () => {
      replaceWallet(await api<Wallet>(`/wallets/${wallet.id}`, { method: 'PATCH', body: JSON.stringify(details) }));
      return `${details.name} updated.`;
    });

  const confirmManage = (wallet: Wallet, action: WalletAction) =>
    manage(async () => {
      if (action === 'delete') {
        await api(`/wallets/${wallet.id}`, { method: 'DELETE' });
        setWallets((current) => current.filter((item) => item.id !== wallet.id));
        return `${wallet.ownerName} deleted.`;
      }
      replaceWallet(await api<Wallet>(`/wallets/${wallet.id}/${action}`, { method: 'POST' }));
      return `${wallet.ownerName} ${action}d.`;
    });

  // WalletController::store: the agency's one wallet, named after it, starting at 0.
  const createWallet = () =>
    manage(async () => {
      const wallet = await api<Wallet>('/wallets', { method: 'POST', body: JSON.stringify({}) });
      setWallets((current) => [...current, wallet]);
      return `${wallet.ownerName} created.`;
    });

  const closeTopUp = () => {
    setTopUpOpen(false);
    setTopUpWallet(null);
    setTopUpDone(false);
  };

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <span className="page-rule" aria-hidden="true" />
          <p className="text-xs font-label font-medium uppercase tracking-wider text-foreground-400">
            Payments
          </p>
          <h1 className="mt-1 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
            Wallets
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-foreground-600">
            The agency float you pay from, topped up per settlement currency.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" icon="ri-refresh-line" disabled={refreshing || loading} onClick={refresh}>
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </Button>
          <Button icon="ri-add-line" disabled={!canFund} onClick={() => openTopUp(null)}>
            Fund wallet
          </Button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FundingBalanceCard totalKes={totals.fundingKes} wallets={fundingOnly} />
        <StatCard
          label="Funding wallets"
          value={String(totals.fundingCount)}
          icon="ri-bank-card-line"
          tone="secondary"
          hint="One float per settlement currency"
        />
      </div>

      <div className="mt-6">
        <WalletQuickActions />
      </div>

      {notice ? (
        <div className="mt-5 flex items-center justify-between gap-3 rounded-md border border-accent-200 bg-accent-50 px-4 py-2.5">
          <span className="inline-flex items-center gap-2 text-sm text-accent-800">
            <i className="ri-checkbox-circle-line" />
            {notice}
          </span>
          <button
            type="button"
            onClick={() => setNotice('')}
            className="cursor-pointer text-accent-700 hover:text-accent-900"
            aria-label="Dismiss"
          >
            <i className="ri-close-line" />
          </button>
        </div>
      ) : null}

      {error ? (
        <div
          role="alert"
          className="mt-5 flex items-center justify-between gap-3 rounded-md border border-primary-200 bg-primary-50 px-4 py-2.5"
        >
          <span className="inline-flex items-center gap-2 text-sm text-primary-800">
            <i className="ri-error-warning-line" />
            {error}
          </span>
          <button
            type="button"
            onClick={() => setError('')}
            className="cursor-pointer text-primary-700 hover:text-primary-900"
            aria-label="Dismiss"
          >
            <i className="ri-close-line" />
          </button>
        </div>
      ) : null}

      {!canFund ? (
        <div className="mt-5 flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-4 py-2.5 text-sm text-foreground-600">
          <i className="ri-lock-line" />
          Your current role can view wallets but not fund them. Switch to Finance or Administrator to
          top up a wallet.
        </div>
      ) : null}

      <div className="mt-6">
        <WalletToolbar
          currency={currency}
          onCurrency={setCurrency}
          markets={markets}
          onSetMarkets={setMarkets}
          query={query}
          onQuery={setQuery}
          currencies={currencyOptions}
        />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-6">
          {loading ? (
            <div className="rounded-lg border border-background-200 bg-background-50 px-5 py-12 text-center text-sm text-foreground-500">
              Loading wallets…
            </div>
          ) : fundingWallets.length > 0 ? (
            <FundingWalletList
              wallets={fundingWallets}
              topUps={topUps}
              canFund={canFund}
              onTopUp={(wallet) => openTopUp(wallet)}
              canManage={canManage}
              onManage={(wallet, action) => setManaged({ wallet, action })}
            />
          ) : fundingOnly.length === 0 && !error ? (
            <div className="rounded-lg border border-background-200 bg-background-50 px-5 py-12 text-center">
              <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-md bg-background-100 text-lg text-foreground-400">
                <i className="ri-safe-2-line" />
              </span>
              <p className="mt-3 text-sm font-medium text-foreground-800">This agency has no wallet yet</p>
              <p className="mx-auto mt-1 max-w-sm text-xs text-foreground-500">
                Payments are made from the agency&apos;s wallet. Create it here; it starts with a balance of 0.
              </p>
              {canManage ? (
                <div className="mt-4 flex justify-center">
                  <Button icon="ri-add-line" disabled={managing} onClick={() => void createWallet()}>
                    {managing ? 'Creating…' : 'Create wallet'}
                  </Button>
                </div>
              ) : (
                <p className="mt-3 text-xs text-foreground-500">Ask someone who can manage wallets to create it.</p>
              )}
            </div>
          ) : (
            <div className="rounded-lg border border-background-200 bg-background-50 px-5 py-12 text-center">
              <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-md bg-background-100 text-lg text-foreground-400">
                <i className="ri-safe-2-line" />
              </span>
              <p className="mt-3 text-sm font-medium text-foreground-800">
                No funding wallets match these filters
              </p>
              <p className="mt-1 text-xs text-foreground-500">
                Clear the currency, market or search filters to see every funding wallet.
              </p>
            </div>
          )}
        </div>

        <div>
          <WalletAttentionPanel wallets={fundingOnly} />
        </div>
      </div>

      <WalletManageModal
        wallet={managed?.wallet ?? null}
        action={managed?.action ?? 'edit'}
        busy={managing}
        onClose={() => setManaged(null)}
        onSave={(details) => managed && saveWallet(managed.wallet, details)}
        onConfirm={() => managed && confirmManage(managed.wallet, managed.action)}
      />

      <FundingHelpModal open={helpOpen} wallet={helpWallet} onClose={() => setHelpOpen(false)} />

      {funding ? (
        <TopUpModal
          open={topUpOpen}
          wallets={fundingOnly}
          preselect={topUpWallet}
          funding={funding}
          onClose={closeTopUp}
          onConfirm={confirmTopUp}
          done={topUpDone}
          reference={topUpRef}
        />
      ) : null}
    </div>
  );
}